/**
 * Non-tech F0 — the template gallery shown under the idea box in simple mode (PRD F3.4).
 * Only WhatsApp-first kits, plus a card that always lets the person describe their own idea.
 */
import { PenLine, Sparkles } from 'lucide-react'
import { simpleKits, type StarterKit } from '#/lib/templates'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'

export function SimpleKits({ locale, selectedId, onPick, onOwnIdea, className }: { locale: 'en' | 'id'; selectedId: string | null; onPick: (kit: StarterKit) => void; onOwnIdea: () => void; className?: string }) {
  const kits = simpleKits()
  return (
    <section className={cn('space-y-2.5', className)} aria-label={m.simple_kits_title()} data-testid="simple-kits">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold">
        <Sparkles className="size-4 text-primary" aria-hidden /> {m.simple_kits_title()}
      </h2>
      <p className="text-xs text-muted-foreground">{m.simple_kits_hint()}</p>
      <ul className="grid gap-2 sm:grid-cols-2">
        {kits.map((k) => (
          <li key={k.id}>
            <button
              type="button"
              onClick={() => onPick(k)}
              aria-pressed={selectedId === k.id}
              className={cn('flex h-full w-full flex-col gap-1 rounded-xl border bg-card px-3 py-2.5 text-left transition-colors hover:border-primary/60 hover:bg-muted/50', selectedId === k.id && 'border-primary bg-primary/5')}
            >
              <span className="min-w-0 truncate text-sm font-medium">{k.title[locale]}</span>
              <span className="text-xs leading-snug text-muted-foreground">{k.tagline[locale]}</span>
            </button>
            {k.examples ? (
              <div className="mt-1.5 px-1" data-testid="simple-kit-examples">
                <p className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">{m.simple_kit_examples_title()}</p>
                <ul className="mt-0.5 space-y-0.5">
                  {k.examples[locale].slice(0, 3).map((ex) => (
                    <li key={ex}>
                      <button
                        type="button"
                        title={m.simple_kit_example_hint()}
                        onClick={() => onPick({ ...k, idea: { en: `${k.idea.en}\n\n${k.examples?.en[k.examples.id.indexOf(ex)] ?? ex}`, id: `${k.idea.id}\n\n${ex}` } })}
                        className="w-full rounded-md px-1.5 py-0.5 text-left text-xs text-foreground/80 hover:bg-muted hover:text-foreground"
                      >
                        + {ex}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </li>
        ))}
        <li>
          <button
            type="button"
            onClick={onOwnIdea}
            className="flex h-full w-full flex-col gap-1 rounded-xl border border-dashed bg-card px-3 py-2.5 text-left transition-colors hover:border-primary/60 hover:bg-muted/50"
            data-testid="simple-kit-own"
          >
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <PenLine className="size-3.5 text-muted-foreground" aria-hidden /> {m.simple_kits_own_title()}
            </span>
            <span className="text-xs leading-snug text-muted-foreground">{m.simple_kits_own_desc()}</span>
          </button>
        </li>
      </ul>
    </section>
  )
}
